import {
	bonusquizzes,
	clearView,
	expandedTopicChange,
	keymap,
	locationChange,
	pathmap,
	quizmap,
	trackingWYLOChange,
	userstore,
	viewChange,
	whichActiveView
} from 'lcc-state';
import capitalize from 'lodash-es/capitalize';
import { publish, subscribe } from 'pub-sub';
import { assessment, bonus, posttest, practice, quiz, viewFromLpl } from './lpl';

const log = (name: string) => {
	const { path } = keymap.get(name)!;
	locationChange({ location: path });
};

function setup_locations() {
	keymap.set('', { path: '/', title: '' });
	pathmap.set('/', {
		topic: {},
		view: () => {
			publish('site/homepage');
		}
	});

	keymap.set('my-progress', { path: '/my-progress', title: 'My Progress' });
	pathmap.set(`/my-progress`, {
		topic: {},
		view: () => {
			publish(`site/progress`);
		}
	});

	keymap.set('glossary', { path: '/glossary', title: 'Glossary' });
	pathmap.set(`/glossary`, {
		topic: {},
		view: () => {
			publish(`site/glossary`);
		}
	});

	['explore', 'explore-assess', 'explore-practice', 'glossary', 'posttest', 'practice'].forEach((loc) => {
		const title = capitalize(loc);
		keymap.set(loc, { path: `/${loc}`, title });
		pathmap.set(`/${loc}`, {
			topic: {},
			view: () => {
				publish(`site/${loc.split('-').join('/')}`);
			}
		});
	});
	keymap.set('explore', { path: '/explore', title: 'Explore The Study Materials' });
}

export function setup_actions(): void {
	subscribe('site/homepage', () => {
		publish('close-all/panes');
		clearView();
		log('explore');
	});
	subscribe('site/quiz', (_topic, ...rest) => {
		const [topicpath, quizid] = rest;
		const title = '';
		viewChange({ view: viewFromLpl(quiz(quizid, title)) });
		publish('close-all/panes');
		locationChange({ location: `${topicpath}/quiz` });
	});
	subscribe('site/assessment', (topic, quiz) => {
		if (quiz) {
			const { title } = keymap.get(quiz)!;
			viewChange({ view: viewFromLpl(assessment(quiz, title)) });
			publish('close-all/panes');
			log(quiz);
		}
	});
	for (const id in quizmap) {
		if (/pre-test/.test(id)) {
			const topic = `site/${id.split('-').join('/')}`;
			subscribe(topic, () => {
				const { quiz } = quizmap.get(id) || {};
				if (quiz) {
					const { title } = keymap.get(id)!;
					viewChange({ view: viewFromLpl(assessment(quiz, title)) });
					publish('close-all/panes');
					log(id);
				}
			});
		}
	}
	subscribe('site/practice', () => {
		const { quiz } = quizmap.get('practice') || {};
		if (quiz) {
			const { title } = keymap.get('practice')!;
			viewChange({ view: viewFromLpl(practice(quiz, title)) });
			publish('close-all/panes');
			log('practice');
		}
	});
	subscribe('site/posttest', () => {
		const { quiz } = quizmap.get('posttest') || {};
		if (quiz) {
			const { title } = keymap.get('posttest')!;
			viewChange({ view: viewFromLpl(posttest(quiz, title)) });
			publish('close-all/panes');
			log('posttest');
		}
	});
	subscribe('site/bonusquiz', (topic, quizid) => {
		const { path, title } = bonusquizzes.get(quizid) || { title: '' };
		if (path) {
			viewChange({ view: viewFromLpl(bonus(quizid, title)) });
			publish('close-all/panes');
			log(quizid);
		}
	});

	subscribe('site/explore', (topic) => {
		const [, , rest] = topic.split('/');
		const location = rest ? `explore-${rest}` : 'explore';
		publish('close-all/panes');
		clearView();
		if (rest && (rest === 'assess' || rest === 'practice')) {
			whichActiveView({ whichActive: rest });
		}
		log(location);
	});

	subscribe('site/glossary/pane', () => {
		publish('close-all/panes');
		log('glossary');
		publish('glossary/open');
	});
	subscribe('site/video', (topic) => {
		const [, , videoid] = topic.split('/');
		publish('close-all/panes');
		publish('video/open', videoid);
	});
	subscribe('site/rcs', (topic) => {
		const [, , ...rest] = topic.split('/');
		const rcspath = `/${rest.join('/')}`;
		publish('close-all/panes');
		publish('rcs/open', rcspath);
	});
	subscribe('site/certificate', () => {
		publish('close-all/panes');
		publish('cert/open');
	});
	subscribe('site/evaluation', () => {
		publish('close-all/panes');
		publish('eval/open');
	});
	subscribe('site/activity', (topic) => {
		const [, , ciaid] = topic.split('/');
		locationChange({ location: `/activity/${ciaid}` });
		viewChange({
			view: {
				widget: 'LearningActivity',
				params: { activityname: ciaid.replaceAll('-', '_') }
			}
		});
	});
	subscribe('site/rise', (topic) => {
		const [, , ciaid] = topic.split('/');
		locationChange({ location: `/rise/${ciaid}` });
		viewChange({
			view: {
				widget: 'RiseActivation',
				params: { activityname: ciaid.replaceAll('-', '_') }
			}
		});
	});

	subscribe('site/landingpage', () => {
		publish('close-all/panes');
		viewChange({
			view: {
				widget: 'LandingPage',
				params: {}
			}
		});
	});

	subscribe('rcs/view', (topic, payload) => {
		const [, , ...rest] = topic.split('/');
		const rcspath = `/${rest.join('/')}`;
		const title = payload?.title;
		locationChange({ location: rcspath });
		publish('close-all/panes');
		viewChange({
			view: {
				widget: 'ResourceCenter',
				params: { baseurl: rcspath, url: rcspath, title }
			}
		});
	});

	subscribe('mystudyplan/view', () => {
		const { get: uget, path: upath } = userstore;
		const { complete: landingCompleted = false } = uget(upath('toolstate', 'landing'));
		publish('close-all/panes');
		expandedTopicChange({ topicid: '' });
		locationChange({ location: '/explore' });
		trackingWYLOChange({ tracking: {} });
		clearView();
		if (!landingCompleted) {
			publish('site/landingpage');
		} else {
			publish('active-view', 'study');
		}
	});

	subscribe('explore/active', () => {
		publish('close-all/panes');
		clearView();
		viewChange({
			view: {
				widget: 'ActiveView',
				params: {}
			}
		});
	});

	subscribe('site/glossary', () => {
		locationChange({ location: '/glossary' });
		publish('close-all/panes');
		viewChange({
			view: {
				widget: 'GlossaryView',
				params: {}
			}
		});
	});

	subscribe('site/progress', () => {
		publish('close-all/panes');
		clearView();
		locationChange({ location: '/my-progress' });
		viewChange({
			view: {
				widget: 'ProgressView',
				params: {}
			}
		});
	});

	setup_locations();
}
